/**
 * **What a screen owes a bar it pins over itself.**
 *
 * The member app has two `position: fixed` bars that sit over scrolling content — My Account's
 * Cancel / Save footer (`MEM-007` AC-4) and the event page's Reserve my spot bar (`MEM-020`
 * AC-1) — plus the shell's own tab bar, which every screen already clears. A fixed bar is
 * removed from flow, so the content underneath it has to buy back the space itself. Where it
 * does not, the last thing on the page is behind the bar at maximum scroll and no amount of
 * scrolling brings it out.
 *
 * That had happened once and nearly twice, so the numbers are written down here rather than
 * appearing as a `pb-*` somebody chose by eye. Each one below was **measured**, not estimated:
 * the app's own compiled stylesheet over the real markup at 375×812 and 1280×800, on
 * 2026-09-01. The measurement is repeatable — that is what {@link MEMBER_PINNED_BAR_RULE} is
 * for — and `a-pinned-bar-does-not-eat-the-page.test.tsx` fails if a class named here and a
 * class rendered on the screen stop agreeing.
 *
 * ## The rule
 *
 * A bar occupies the band from `offset` to `offset + height`, measured up from the bottom of the
 * viewport. Two paddings clear it, and both are real:
 *
 *   - the **shell's**, on `MemberLayout`'s `<main>`, which every screen gets for free and which
 *     exists to clear the tab bar; and
 *   - the **screen's own**, on whatever element carries the scrolling content.
 *
 * The content is clear when `shellPadding + screenPadding >= offset + height`. Stated that way
 * rather than as "the screen's padding must be at least the bar's height" because the shell's
 * padding is genuinely part of the answer, and a rule that pretended otherwise would demand a
 * screen padding large enough to leave a visible strip of nothing at the foot of every page.
 *
 * ## Why pixels and not a Tailwind class
 *
 * Tailwind emits only the classes it can literally see in a source file, so a class name that is
 * computed at runtime compiles to nothing at all. Every class below is therefore written out as
 * a string literal and *also* given its value in pixels; {@link tailwindPaddingBottomPx} reads
 * the one from the other, so the two cannot drift apart without the test saying so.
 */

/** One step of Tailwind's default spacing scale. `pb-4` is four of them. */
export const TAILWIND_SPACING_STEP_PX = 4;

/**
 * `MemberLayout`'s `<main>` padding — the shell's half of the arithmetic above.
 *
 * It is here, and imported by the layout, so that this module and the shell cannot disagree
 * about a number both of them depend on. Changing it changes the clearance of every screen at
 * once, which is exactly the blast radius it should have.
 *
 * The phone value clears the tab bar (measured at **61.5px**); the desktop value has no tab bar
 * to clear — it is `md:hidden` — and is ordinary page bottom margin: `Web App.dc.html`'s content
 * container ends in `padding-bottom:80px` before the in-app footer (10 September), which is
 * `md:pb-20`. It was `md:pb-16` before the footer existed; the change only adds clearance.
 */
export const MEMBER_SHELL_MAIN_PADDING_CLASS = 'pb-24 md:pb-20';

export interface PinnedBarGeometry {
  /** Where the bar's underside sits, in pixels above the bottom of the viewport. */
  readonly offsetPx: number;
  /**
   * The bar's own height in pixels, **in its tallest state**.
   *
   * Tallest matters: My Account's footer grows by the "Saved." line after a successful save, and
   * a clearance computed against the short state puts the bug back the first time a member saves
   * something.
   */
  readonly heightPx: number;
}

/**
 * The pinned bars, phone and desktop, as measured.
 *
 * `desktop` is a separate entry rather than a multiplier because the two differ in kind, not in
 * degree: on `md` and up the tab bar is hidden, so a bar that stays at its phone offset floats
 * above a strip of nothing with the page scrolling visibly underneath it. The save bar therefore
 * drops to `bottom-0` at `md`, and its desktop offset is zero.
 *
 * **`wide` is `lg` and up, and `null` there is a bar that is not pinned at that width.** Both bars
 * are `null` there now. The event page's reserve action is pinned on a phone and a tablet and,
 * from `lg`, in flow in the sticky sidebar the 10 September desktop layout draws (`MEM-056`
 * AC-4); My Account's Save and Cancel are pinned on a phone and a tablet and, from `lg`, in flow
 * inside the profile card where §2.15 draws them (Sumit, 2026-09-21, TW 44145291). Either way
 * there is nothing over the page to clear and the rule has no arithmetic to do. The test asserts
 * the bar really is `lg:static`, so a `null` here cannot hide a bar that is still pinned.
 */
export const MEMBER_PINNED_BARS = {
  /**
   * My Account's Cancel / Save footer (`MEM-007` AC-4).
   *
   * `83px` is the footer with its "Saved." status line showing: `py-3` (24) + an `h-9` control
   * row (36) + `mt-1` (4) + the `text-xs` line (18) + the 1px top border. Without the status
   * line it measures 61px.
   */
  saveBar: {
    phone: { offsetPx: 64, heightPx: 83 },
    desktop: { offsetPx: 0, heightPx: 83 },
    /**
     * **Not pinned from `lg`** since 2026-09-21 (TW 44145291). Sumit's decision follows §2.15 on
     * the desktop, where *Save changes* is a pill inside the profile card and there is no bar; the
     * markup is the last thing in that card and goes `lg:static` there, which is the shape ruling
     * 15 gave the reserve action. So there is nothing over the page to clear at that width, and
     * the form's own padding drops to `lg:pb-8` rather than reserving a bar's worth of nothing.
     */
    wide: null,
  },
  /**
   * The event page's Reserve my spot bar (`MEM-020` AC-1).
   *
   * `102px` on a phone: `pt-6` (24) + an `h-10` button (40) + `mt-2` (8) + the `text-xs` refund
   * line (18) + `pb-3` (12). From `md` `md:pb-5` makes it 110.
   *
   * Its offset is `62px` rather than the save bar's 64 — the bar predates this module and the
   * two were each chosen against the tab bar by eye. Both clear it; neither is wrong enough to
   * be worth a change that would have to be re-reviewed against the prototype.
   *
   * **Not pinned from `lg`** since the 10 September layout: `lg:static`, in the sidebar. It used
   * to stay pinned at every desktop width, a 390px bar centred under a 1120px page.
   */
  reserveBar: {
    phone: { offsetPx: 62, heightPx: 102 },
    desktop: { offsetPx: 0, heightPx: 110 },
    wide: null,
  },
} as const satisfies Record<
  string,
  { phone: PinnedBarGeometry; desktop: PinnedBarGeometry; wide: PinnedBarGeometry | null }
>;

/**
 * Where My Account's footer is pinned.
 *
 * **`md:bottom-0` is a fix, not a tidy-up.** The bar was `bottom-16` at every width, and that
 * `16` exists only to clear the phone tab bar — which is `md:hidden`. On a desktop window the
 * bar therefore floated 64px up from the bottom of the viewport with the page scrolling
 * *visibly* underneath it, so the card the member was reading was cut across the middle by an
 * opaque bar and reappeared below it. Measured at 1280×800 on 2026-09-01.
 */
export const MEMBER_SAVE_BAR_OFFSET_CLASS = 'bottom-16 md:bottom-0';

/**
 * Where My Account's Save and Cancel stop being a bar (Sumit, 2026-09-21, TW 44145291).
 *
 * The same shape ruling 15 gave the event page's Reserve action, at the same line: the markup
 * lives where the design draws it — the last thing inside the profile card — and is lifted out of
 * flow and pinned to the foot of the viewport only up to `lg`. `lg` is also where the screen's
 * two-column layout opens, so above it the card is a column with its own end in view and below it
 * the card is the width of the screen with its foot a long scroll away.
 *
 * Here rather than inline so that this module, which says the bar is unpinned at `wide`, and the
 * screen, which unpins it, cannot disagree.
 */
export const MEMBER_SAVE_BAR_UNPINS_CLASS =
  'lg:static lg:z-auto lg:mt-[22px] lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none';

/**
 * What My Account's form owes its own footer.
 *
 * `pb-28` (112px) against a phone requirement of 147px, of which the shell's `pb-24` already
 * pays 96 — 208 against 147, so the last card clears the bar by 61px at maximum scroll. It was
 * already this value and it already measured clear; what it did not have was any stated reason
 * to be that number rather than another, which is how the event page's equivalent came to be
 * `pb-4` and to hide 52px of itself.
 *
 * **`lg:pb-8` is the other half of unpinning the bar** (TW 44145291): from `lg` the controls are
 * in flow inside the profile card and nothing covers the page, so a footer's worth of padding
 * under the last card would be a visible strip of nothing. `pb-8` is what this form already used
 * on a screen that draws no footer at all — the `MEM-049` branch — so the two agree.
 */
export const MEMBER_SAVE_BAR_CLEARANCE_CLASS = 'pb-28 lg:pb-8';

/** The band a bar covers, measured up from the bottom of the viewport. */
export function coveredHeightPx(bar: PinnedBarGeometry): number {
  return bar.offsetPx + bar.heightPx;
}

/**
 * Reads the phone and desktop `padding-bottom` a Tailwind class list declares, in pixels.
 *
 * `pb-24 md:pb-16` answers `{ phone: 96, desktop: 64 }`. A list with no `md:` variant answers
 * the same value twice, which is what an unprefixed utility actually does.
 *
 * Returns `null` for a list with no `pb-*` at all, so a caller can tell "no padding was
 * declared" apart from "a padding of zero was declared" — the first is the defect this module
 * exists for and it deserves its own failure message.
 */
export function tailwindPaddingBottomPx(
  classList: string,
): { readonly phone: number; readonly desktop: number } | null {
  let phone: number | null = null;
  let desktop: number | null = null;

  for (const token of classList.split(/\s+/u)) {
    const match = /^(?:(md):)?pb-(\d+)$/u.exec(token);
    if (match === null) continue;
    const px = Number(match[2]) * TAILWIND_SPACING_STEP_PX;
    if (match[1] === 'md') desktop = px;
    else phone = px;
  }

  if (phone === null && desktop === null) return null;
  return { phone: phone ?? 0, desktop: desktop ?? phone ?? 0 };
}

/** The rule, as prose, so a failing test can quote it rather than restate it. */
export const MEMBER_PINNED_BAR_RULE =
  'The shell padding plus the screen’s own padding must be at least the bar’s offset plus its height, or the foot of the page is behind the bar at maximum scroll.';
